<template>
  <BModal
    v-model="showModalInternal"
    :title="`Uprawnienia roli: ${role && role.name ? role.name : ''}`"
    hide-footer
    scrollable
    size="xl"
  >
    <div v-if="isRoleAdmin" class="alert alert-warning d-flex align-items-center mb-3">
      <i class="fas fa-lock me-2"></i>
      <span>ROLE_ADMIN jest rolą systemową i nie może być modyfikowana.</span>
    </div>

    <div class="card shadow-sm mb-3">
      <div class="card-header bg-light">
        <h6 class="mb-0 text-primary fw-bold">
          <i class="fas fa-shield-alt me-2"></i>Zarządzanie uprawnieniami roli
        </h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Rola</label>
            <input type="text" class="form-control" :value="role && role.name ? role.name : ''" disabled />
          </div>
          <div class="col-md-6">
            <label class="form-label">Szukaj uprawnień</label>
            <input
              type="text"
              class="form-control"
              v-model="searchFilter"
              placeholder="Filtruj po nazwie lub opisie..."
            />
          </div>
        </div>
      </div>
    </div>

    <CommonTable2
      :key="tableKey"
      :table="tableConfig"
      rowIdentifier="id"
      :currentPage="currentPage"
      :totalRows="filteredTotalRows"
      :totalPages="filteredTotalPages"
      :showPaginationBottom="true"
      :showPaginationTop="true"
      :showMassActions="false"
      :multiselect="false"
      :showRowActions="true"
      @onPageChange="handlePageChange"
      @onSort="handleSort"
      @onItemAction="handleItemAction"
      :per-page="pageSize"
    />

    <div class="d-flex justify-content-end mt-3">
      <button type="button" class="btn btn-secondary" @click="showModalInternal = false">
        Zamknij
      </button>
    </div>
  </BModal>
</template>

<script>
import { BModal } from "bootstrap-vue-next";
import CommonTable2 from "@/components/widgets/CommonTable2.vue";
import { useUserRbacManagerModule } from "@/state/pinia/userRbacManagerModule";
import Swal from "sweetalert2";

export default {
  name: "RolePrivilegesModal",
  components: { BModal, CommonTable2 },
  props: {
    showModal: { type: Boolean, required: true },
    role: { type: Object, default: () => ({}) },
  },
  emits: ["update:showModal", "onSaved"],
  data() {
    return {
      showModalInternal: this.showModal,
      assignedPrivilegeIds: new Set(),
      allPrivilegesOnPage: [],
      currentPage: 1,
      totalRows: 0,
      totalPages: 1,
      pageSize:20,
      sortColumn: "id",
      sortingDirection: "ASC",
      searchFilter: "",
      tableKey: 0,
      tableConfig: {
        headers: [
          { key: "id", text: "ID", type: "text", sortable: true, width: "80px" },
          { key: "name", text: "Nazwa", type: "text", sortable: true, width: "200px" },
          { key: "displayName", text: "Nazwa wyświetlana", type: "text", sortable: false, width: "220px" },
          { key: "description", text: "Opis", type: "text", sortable: false, width: "260px" },
          { key: "actions", text: "Akcje", type: "actions", sortable: false, width: "120px" },
        ],
        data: [],
      },
    };
  },
  computed: {
    rbacModule() {
      return useUserRbacManagerModule();
    },
    isRoleAdmin() {
      return this.role && this.role.name === "ROLE_ADMIN";
    },
    resolvedRoleId() {
      return this.role && (this.role.id || this.role.roleId || this.role.guid);
    },
    filteredPrivileges() {
      if (!this.searchFilter) return this.allPrivilegesOnPage;
      const search = this.searchFilter.toLowerCase();
      return this.allPrivilegesOnPage.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(search) ||
          (p.displayName || "").toLowerCase().includes(search) ||
          (p.description || "").toLowerCase().includes(search)
      );
    },
    filteredTotalRows() {
      if (this.searchFilter) return this.filteredPrivileges.length;
      return this.totalRows;
    },
    filteredTotalPages() {
      if (this.searchFilter) return 1;
      return this.totalPages;
    },
  },
  watch: {
    showModal(newValue) {
      this.showModalInternal = newValue;
      if (newValue) {
        this.initFromRole();
        this.refreshPrivilegesList();
      }
    },
    showModalInternal(newValue) {
      this.$emit("update:showModal", newValue);
    },
    searchFilter() {
      this.buildTableData(this.filteredPrivileges);
    },
  },
  methods: {
    initFromRole() {
      const privileges = (this.role && (this.role.privileges || this.role.privilegeKeys)) || [];
      if (Array.isArray(privileges)) {
        this.assignedPrivilegeIds = new Set(
          privileges
            .map((p) => (typeof p === "object" && p !== null ? p.id : null))
            .filter(Boolean)
        );
      } else {
        this.assignedPrivilegeIds = new Set();
      }
    },
    resolvePrivilegeName(privilege) {
      return privilege.name || privilege.key || privilege.privilegeKey || "";
    },
    buildTableData(privileges) {
      this.tableConfig.data = privileges.map((privilege) => {
        const assigned = this.assignedPrivilegeIds.has(privilege.id);
        return {
          id: privilege.id,
          name: privilege.name || "-",
          displayName: privilege.displayName || "-",
          description: privilege.description || "-",
          actions: [
            {
              icon: assigned ? "fas fa-times-circle" : "fas fa-plus-circle",
              action: assigned ? "remove-privilege" : "add-privilege",
              tooltip: assigned ? "Usuń z roli" : "Dodaj do roli",
              enabled: !this.isRoleAdmin,
              class: assigned
                ? "btn btn-sm btn-outline-danger"
                : "btn btn-sm btn-outline-success",
            },
          ],
        };
      });
      this.tableKey++;
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.refreshPrivilegesList();
    },
    handleSort(sortingKeyValue) {
      this.sortColumn = sortingKeyValue;
      this.currentPage = 1;
      this.refreshPrivilegesList();
    },
    handleItemAction(event) {
      const privilege = event.itemData;
      if (event.action.action === "add-privilege") {
        this.addPrivilege(privilege);
      } else if (event.action.action === "remove-privilege") {
        this.removePrivilege(privilege);
      }
    },
    addPrivilege(privilege) {
      if (!this.resolvedRoleId || this.isRoleAdmin) return;
      const privilegeKey = this.resolvePrivilegeName(privilege);
      if (!privilegeKey) return;
      this.rbacModule
        .httpUpdateRolePrivilegesAdd({
          id: this.resolvedRoleId,
          privilegeKeys: [privilegeKey],
        })
        .then(() => {
          this.assignedPrivilegeIds.add(privilege.id);
          this.buildTableData(this.filteredPrivileges);
          this.$emit("onSaved");
        });
    },
    removePrivilege(privilege) {
      if (!this.resolvedRoleId || this.isRoleAdmin) return;
      const privilegeKey = this.resolvePrivilegeName(privilege);
      if (!privilegeKey) return;
      Swal.fire({
        title: "Usunąć uprawnienie?",
        text: `Czy na pewno chcesz usunąć uprawnienie "${privilege.displayName !== "-" ? privilege.displayName : privilege.name}" z roli?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        confirmButtonText: "Usuń",
        cancelButtonText: "Anuluj",
      }).then((result) => {
        if (result.isConfirmed) {
          this.rbacModule
            .httpUpdateRolePrivilegesRemove({
              id: this.resolvedRoleId,
              privilegeKeys: [privilegeKey],
            })
            .then(() => {
              this.assignedPrivilegeIds.delete(privilege.id);
              this.buildTableData(this.filteredPrivileges);
              this.$emit("onSaved");
            });
        }
      });
    },
    refreshPrivilegesList() {
      this.rbacModule
        .httpGetPrivilegesPaging({
          currentPage: this.currentPage,
          sortingKey: this.sortColumn,
          sortingDirection: this.sortingDirection,
          pageSize:this.pageSize
        })
        .then(() => {
          this.allPrivilegesOnPage = [...this.rbacModule.getPrivileges];
          this.totalPages = this.rbacModule.getPrivilegesPagesCount || 1;
          this.totalRows = this.rbacModule.getPrivilegesDataTotal || 0;
          this.buildTableData(this.filteredPrivileges);
        });
    },
  },
  created() {
    if (this.showModal) {
      this.initFromRole();
      this.refreshPrivilegesList();
    }
  },
};
</script>
